import { FloatingPortal } from '@floating-ui/react'
import { Combobox, Disclosure } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Check } from 'iconoir-react'
import { entries } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ComboBoxInputSearch } from './styled/ComboBoxInputSearch'
import { CustomOption } from './styled/CustomOption'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'

type Props = {
  options?: OptionType[]
  comboDropdownInputRef: React.MutableRefObject<HTMLInputElement>
  floatingStyles: React.CSSProperties
  floatingProps: Record<string, unknown>
  open: boolean
  showSearchInDropdown?: boolean
  value?: OptionType
  onClearSearch?: () => void
  setPopperElement: (element: HTMLUListElement | null) => void
}
export function GroupedComboOptions({
  options,
  comboDropdownInputRef,
  floatingStyles,
  floatingProps,
  open,
  showSearchInDropdown = true,
  value,
  onClearSearch,
  setPopperElement,
}: Props): ReactElement {
  const groups = useMemo(
    () =>
      options?.reduce((groups, option) => {
        let group = option.meta?.group ?? `Other`
        if (groups[group] == null) {
          groups[group] = [option]
        } else {
          groups[group].push(option)
        }
        return groups
      }, {} as Record<string, OptionType[]>),
    [options],
  )
  const [filteredOptions, setFilteredOptions] = useState<Record<string, OptionType[]> | undefined>(groups)

  const preventScrollingToInput = () => {
    comboDropdownInputRef.current?.focus({ preventScroll: true })
  }
  const handleSearchValueChange = (text: string) => {
    // onSearchValueChange?.(text)
    if (text.length === 0) {
      setFilteredOptions(groups)
      return
    }
    setFilteredOptions(() => {
      let filtered = { ...groups }
      entries(filtered).forEach(([group, options]) => {
        filtered[group] = options.filter((option) => option.name.toLowerCase().includes(text.toLowerCase()))
        if (filtered[group].length === 0) {
          delete filtered[group]
        }
      })
      return filtered
    })
  }
  useEffect(() => {
    //hack to reset the filtered options when the dropdown is closed
    if (!open) setFilteredOptions(groups)
  }, [open])

  debug(`groups`, `combobox grouped options`, groups, `filtered grouped options`, filteredOptions)
  return open ? (
    <FloatingPortal>
      <Combobox.Options
        className={twMerge(`combobox-options`, `cursor-pointer`)}
        onWheel={(evt) => evt.stopPropagation()}
        ref={setPopperElement}
        style={floatingStyles}
        {...floatingProps}
        static
      >
        {showSearchInDropdown && (
          <ComboBoxInputSearch
            comboDropdownInputRef={comboDropdownInputRef}
            handleSearchValueChange={handleSearchValueChange}
            onClearSearch={() => {
              setFilteredOptions(groups)
              onClearSearch?.()
            }}
          />
        )}
        {entries(filteredOptions)?.map(([group, options], i) => {
          return (
            <Disclosure key={group} defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    className={twMerge(
                      `flex items-center justify-between py-2 pl-4 pr-[6px]`,
                      i !== 0 ? `border-t border-neutral-200` : ``,
                    )}
                  >
                    <span className="text-ui-sm text-neutral-900">{group}</span>
                    <ChevronDownIcon
                      className={twMerge(
                        `h-5 w-5`,
                        filteredOptions && filteredOptions[group].length > 0 && open ? `rotate-180 transform` : ``,
                      )}
                    />
                  </Disclosure.Button>

                  <Disclosure.Panel>
                    {options?.map((option) => (
                      <div
                        className="h-8 text-body-sm leading-8 text-neutral-800"
                        key={`${option.id}-${option.name}-${option.value}`}
                      >
                        <Combobox.Option
                          value={option}
                          onClick={() => preventScrollingToInput()}
                          className={({ active }) => twMerge(`pl-4 pr-[6px]`, active ? `bg-trn-100 ` : ``)}
                        >
                          {option.meta.customComboOption ? (
                            <CustomOption selected={value && value.id === option.id}>
                              {option.meta.customComboOption}
                            </CustomOption>
                          ) : (
                            <div className={twMerge(`flex w-full items-center justify-between`)}>
                              <span className={twMerge(`flex items-center whitespace-pre-wrap`)}>{option.name}</span>
                              <Check
                                className={twMerge(
                                  `h-5 w-5`,
                                  value && value.id === option.id ? `ml-4 block` : `hidden`,
                                )}
                              />
                            </div>
                          )}
                        </Combobox.Option>
                      </div>
                    ))}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          )
        })}
      </Combobox.Options>
    </FloatingPortal>
  ) : (
    <></>
  )
}
